import { FC, Key, useState } from "react";
import * as _ from "lodash";

import { Col, Row, Analytics, Charts, DndProvider, HTML5Backend } from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Enterprise, SavedChartState, SavedChartsPayload } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";

import { CompiledRoutes } from "@routes";

import { useFarm } from "@farms";
import { useDataDictionary } from "@base";
import { GAActions, formatFilters, useSavedCharts } from "@analytics";
import { Chart, ChartAPIContext, ChartFilterContext } from "@chart";

import styles from "./ChartList.module.less";

const {
  V3: { ChartCard }
} = Analytics;

interface ChartListProps {
  charts: SavedChartsPayload;
  groupName: string;
}

/**
 * List of chart cards
 * @returns
 */
export const ChartList: FC<ChartListProps> = ({ charts, groupName }) => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { farm } = useFarm();
  const searchParams = new URLSearchParams();
  const [order, setOrder] = useState<Key[]>([]);

  const {
    metrics,
    dataDescriptors,
    api: { extractSource }
  } = useDataDictionary();

  const {
    api: { removeChart, updateChartOrder }
  } = useSavedCharts();

  const {
    analytics: {
      config: {
        metric: { displayName },
        variant: variantCopy,
        source: sourceCopy
      }
    }
  } = Content;

  /**
   * Route to configurator with the chart
   * @param {ReturnType<(variantID: MetricEntity["id"]) => ({metric: string, source: string | undefined, variant: string} | undefined)>} chart
   * @param {SavedChartState} savedChartState
   */
  const handleClick = (chart: ReturnType<typeof extractSource>, savedChartState: SavedChartState) => {
    if (chart) {
      chart?.source && searchParams.set("source", chart.source);
      searchParams.set("variant", chart.variant);

      trackEvent(GAActions.goToChartConfig);

      Enterprise.history.push({
        pathname: CompiledRoutes.app.analytics.configure.metric({ metricID: chart.metric }),
        search: searchParams.toString(),
        state: { savedChart: savedChartState }
      });
    }
  };

  const moveNode = (dragKey: Key, hoverKey: Key) => {
    const newOrder = order.slice();

    charts.forEach((item) => {
      if (item.variantId && newOrder.indexOf(item.variantId) === -1) {
        newOrder.push(item.variantId);
      }
    });

    const dragIndex = newOrder.indexOf(dragKey);
    const hoverIndex = newOrder.indexOf(hoverKey);

    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, dragKey);

    updateChartOrder(newOrder, groupName);
    setOrder(newOrder);
  };

  /**
   * Order the charts based on the provided order state
   */
  const orderedCharts = _.sortBy(charts, (chart) => {
    const orderIndex = _.indexOf(order, chart.variantId);
    return orderIndex !== -1 ? orderIndex : charts.indexOf(chart);
  });

  return (
    <Row gutter={[24, 24]}>
      {_.map(orderedCharts, (savedChart) => {
        const { variantId: variantID, dataFilters, displayFilters, dateRange, chartType } = savedChart;
        const chart = extractSource(variantID);

        const familyName =
          chart &&
          _.get(
            _.find(metrics, ({ family }) => _.isEqual(family, chart.metric)),
            "name"
          );
        const metricName = (chart && _.get(metrics, [chart.metric, "name"])) as string;
        const variantType = _.get(dataDescriptors, [variantID, "dataProperties", "type"]);
        const sourceName = chart?.source && _.get(metrics, [chart.source, "sourceTypes", 0]);

        /**
         * dateRange can either b iso duration in an array e.g ["P1M"] or a custom range e.g. ["2023-05-01", "2023-06-01"]
         * Supply props to api context based on length of array
         */
        const isoDuration = dateRange && _.size(dateRange) === 1 ? _.first(dateRange) : null;
        const [startDate, endDate] = dateRange && _.size(dateRange) === 2 ? dateRange : [];
        const durationProps = isoDuration
          ? { isoDuration }
          : startDate && { timePeriod: { startDate, endDate }, isoDuration: null };

        const filters = dataFilters && formatFilters(dataFilters);
        const seriesFilter = _.find(displayFilters, { type: "series" });
        const seriesValues = _.get(seriesFilter, "values", []);

        return (
          chart && (
            <Col
              key={variantID}
              lg={12}
              span={24}
              onClick={(event) => {
                if (_.toLower(_.get(event, ["target", "tagName"])) !== "canvas") {
                  handleClick(chart, {
                    view: chartType,
                    dataFilters,
                    displayFilters,
                    isoDuration,
                    timePeriod: !isoDuration ? { startDate, endDate } : void 0
                  });
                }
              }}
            >
              <ChartFilterContext.Provider series={seriesValues} filters={filters} {...durationProps}>
                <ChartAPIContext.Provider
                  dataDescriptorID={variantID}
                  farmID={farm?.id}
                  defaultView={chartType}
                >
                  <DndProvider backend={HTML5Backend}>
                    <ChartCard
                      index={variantID}
                      className={styles.Card}
                      metric={t(displayName(familyName || metricName), { makeDefaultValue: true })}
                      variant={t(variantCopy(variantType))}
                      source={sourceName && t(sourceCopy(sourceName))}
                      filterCount={_.sumBy(_.values(filters) as Array<string>, (arr) => arr.length)}
                      handleDrag={moveNode}
                      handleDelete={() => {
                        trackEvent(GAActions.removeMetric);
                        removeChart(variantID, groupName);
                      }}
                    >
                      <Col flex={1} style={{ padding: 0 }}>
                        <Chart variant={Charts.ChartVariant.Embedded} viewOptions={{}} />
                      </Col>
                    </ChartCard>
                  </DndProvider>
                </ChartAPIContext.Provider>
              </ChartFilterContext.Provider>
            </Col>
          )
        );
      })}
    </Row>
  );
};
